import { tr } from '../i18n';

// ---------------------------------------------------------------------------
// Central role / access map — SINGLE SOURCE OF TRUTH.
//
// To change who can see a page, edit PAGE_ACCESS below. To change who sees
// ALL stations (vs. only their associated ones), edit GLOBAL_STATION_ROLES.
// Nothing else needs touching — App.js (routes), NavigationBar.js (icons) and
// the report pages (station scoping) all read from here.
//
// Role matrix (backend "Users 1/2.jpeg"):
//   1 Superadmin      — global, can create users
//   2 Manager         — bound to ONE station
//   3 Cluster Manager — bound to MULTIPLE stations
//   4 Admin           — global, can create users
//   5 Auditor         — global report reader
//   6 Manufacture     — bound to ONE station, private deployment only
//   7 Buses Manager   — bound to ONE station, private deployment only
//   8 Buses Accountant — bound to ONE station, private deployment only,
//                        sees ONLY BusesReports (nothing else)
//   9 Manufacture Accountant — bound to ONE station, sees ONLY Reports,
//                        CloseReports, OffloadingReports and TankGauge
//
// NOTE: this is a CONVENIENCE / UX boundary, not a security one. The backend
// only enforces user-creation (POST /auth/newuser → 401 for non-admins); it
// does NOT scope report data by station, so a determined user with a token can
// still query any station directly. Real enforcement must live server-side.
// ---------------------------------------------------------------------------

export const ROLE = {
  SUPERADMIN: 1,
  MANAGER: 2,
  CLUSTER_MANAGER: 3,
  ADMIN: 4,
  AUDITOR: 5,
  MANUFACTURE: 6,
  BUSES_MANAGER: 7,
  BUSES_ACCOUNTANT: 8,
  MANUFACTURE_ACCOUNTANT: 9,
};

// Roles 6 (Manufacture) and 7 (Buses Manager) are station-scoped, same as
// Manager - the server treats them exactly as a Manager (station-scoped,
// same API permissions); this file reads the name and narrows what the UI
// offers.
//
// ⚠ This is a UX boundary, not a security one - the same caveat as the rest
// of this file, but worth restating here because it is easy to read
// "role 6/7 sees fewer pages" as an enforced restriction. It is not. A
// Manufacture or Buses Manager user holds a Manager token and can call any
// endpoint a Manager can. Hiding pages from their nav does not stop them
// requesting those APIs directly. If any of that actually needs to be
// denied, it has to be enforced server-side against something the backend
// can see.

// Page key -> role ids allowed to see it. Page keys match the route names.
// ★ EDIT HERE to grant/revoke a page for a role — this is the DEFAULT map,
// used for central.fms.tiger4pump.com and as the fallback for any
// unrecognized domain.
const DEFAULT_PAGE_ACCESS = {
  Statistics:        [ 1,4,5],   // read-only overview, all roles
  StationMap:        [ 1, 3,4,  5],  // map of stations + litres (Cluster Manager sees own stations)
  Reports:           [ 1,2, 3,4,  5,6,7,9], // individual sales
  PeriodicReports:   [ 1,2, 3,4,  5,7],
  CloseReports:      [ 1,2, 3,4,  5,6,7,9],
  OffloadingReports: [ 1,2, 3,4,  5,6,7,9],
  TankGauge:         [ 1,2, 3,4 , 5,6,7,9], // "Monitor"
  Settings:          [ 1,2, 3,4,5,7],    // "Stations" list (Auditor excluded)
  Stations:          [ 1,2, 3,4,5,7],
  Users:             [ 1,4],          // create users (Superadmin )
  Cards:             [ 1,4,7],    // fuel cards — customer credit, no Auditor
  AttendantCards:    [ 1,4,7],    // pump attendant cards
  Orders:            [ 1,2,7],
  // Orders intentionally absent here — not shown on the default/central
  // deployment at all, for any role. Still present in PRIVATE_PAGE_ACCESS
  // below, so it only shows on the private deployment.
};

// ★ EDIT HERE for the private deployment's own access rules — starts as an
// identical copy of DEFAULT_PAGE_ACCESS so nothing changes until you
// actually customize entries below. Independent from DEFAULT_PAGE_ACCESS:
// editing one does NOT affect the other.
const PRIVATE_PAGE_ACCESS = {
  Statistics:        [ 1,4,5],
  StationMap:        [ 1, 3,4,  5],
  Reports:           [ 1,2, 3,4,  5,6,7,9],
  PeriodicReports:   [ 1,2, 3,4,  5,7],
  CloseReports:      [ 1,2, 3,4,  5,6,7,9],
  OffloadingReports: [ 1,2, 3,4,  5,6,7,9],
  TankGauge:         [ 1,2, 3,4 , 5,6,7,9],
  Settings:          [ 1,2, 3,4,7],
  Stations:          [ 1,2, 3,4,7],
  Users:             [ 1],
  Cards:             [ 1,2,4,7],
  AttendantCards:    [ 1,2,4],
  Orders:            [ 1,2,4,6,7],
  // Manager-only (role 2), plus role 7 (Buses Manager) which was built
  // specifically for this feature - route_report.py, this feature's
  // backend, only exists on the private server, so this key is
  // intentionally absent from DEFAULT_PAGE_ACCESS (same reasoning as
  // Orders being absent there) - a page with no backend to call would be
  // worse than no page.
  BusesReports:      [ 1,2,7,8],
};

// Domain -> which access map that domain uses. Mirrors config.js's API_MAP
// pattern exactly — same domains, same resolution approach, so the two
// stay easy to reason about side by side.
const PAGE_ACCESS_MAP = {
  'central.fms.tiger4pump.com':         DEFAULT_PAGE_ACCESS,
  'central.private.fms.tiger4pump.com': PRIVATE_PAGE_ACCESS,

  // Local/dev fallback — adjust as needed while testing on localhost.
  // Local/dev fallback - points at PRIVATE so local testing sees the same
  // pages the private deployment does (Orders, BusesReports, etc.), which
  // DEFAULT_PAGE_ACCESS deliberately never has. Switch back to
  // DEFAULT_PAGE_ACCESS here if local dev should instead mirror the
  // default/central deployment.
  'localhost': PRIVATE_PAGE_ACCESS,
};

const _host = typeof window !== 'undefined' ? window.location.hostname : '';

export const PAGE_ACCESS = PAGE_ACCESS_MAP[_host] || DEFAULT_PAGE_ACCESS;

if (typeof window !== 'undefined' && !PAGE_ACCESS_MAP[_host]) {
  // eslint-disable-next-line no-console
  console.warn(
    `[roleAccess] Unrecognized hostname "${_host}" — falling back to the default PAGE_ACCESS. ` +
    `Add it to PAGE_ACCESS_MAP in src/roleAccess.js if this is a new deployment.`
  );
}

// ---------------------------------------------------------------------------
// Page key -> display label, same domain-scoped pattern as PAGE_ACCESS above.
// NavigationBar.js reads these instead of hardcoding label text, so each
// deployment (central vs private) can show different wording for the same
// page without touching NavigationBar.js itself.
// ★ EDIT HERE to rename a page's label for the default (central) deployment.
// ---------------------------------------------------------------------------
const DEFAULT_PAGE_NAMES = {
  Statistics:        'DASHBOARD',
  StationMap:        'STATION MAP',
  Reports:           'TRA RECEIPTS',
  PeriodicReports:   'PERIODIC REPORTS',
  CloseReports:      'CLOSE REPORTS',
  OffloadingReports: 'OFFLOADING REPORTS',
  TankGauge:         'ATG',
  Settings:          'STATIONS',
  Stations:          'STATIONS',
  Users:             'USERS',
  Cards:             'CLIENT CARDS',
  AttendantCards:    'ATTENDANT CARDS',
  Orders:            'ORDERS',
  BusesReports:      'BUSES REPORTS',
};

// ★ EDIT HERE for the private deployment's own labels — starts as an
// identical copy of DEFAULT_PAGE_NAMES, independent from it just like
// PRIVATE_PAGE_ACCESS is independent from DEFAULT_PAGE_ACCESS.
const PRIVATE_PAGE_NAMES = {
  Statistics:        'DASHBOARD',
  StationMap:        'STATION MAP',
  Reports:           'TRANSICTIONS',
  PeriodicReports:   'PERIODIC REPORTS',
  CloseReports:      'CLOSE REPORTS',
  OffloadingReports: 'OFFLOADING REPORTS',
  TankGauge:         'ATG',
  Settings:          'STATIONS',
  Stations:          'STATIONS',
  Users:             'USERS',
  Cards:             'CLIENT CARDS',
  AttendantCards:    'ATTENDANT CARDS',
  Orders:            'ORDERS',
  BusesReports:      'BUSES REPORTS',
};

const PAGE_NAMES_MAP = {
  'central.fms.tiger4pump.com':         DEFAULT_PAGE_NAMES,
  'central.private.fms.tiger4pump.com': PRIVATE_PAGE_NAMES,
  'localhost': PRIVATE_PAGE_NAMES,
};

export const PAGE_NAMES = PAGE_NAMES_MAP[_host] || DEFAULT_PAGE_NAMES;

// Looks up a page's display label for the current deployment. `fallback`
// (optional) is used if the key is somehow missing from the map instead of
// silently rendering nothing; defaults to the key itself.
// The label is translated here (ar.js has every label, e.g. "STATION MAP").
export const pageName = (key, fallback) => tr(PAGE_NAMES[key] || fallback || key);

// Roles that can see EVERY station's data (no station scoping in the UI).
// Everyone else is limited to their associated_station(s).
// ★ EDIT HERE to change global-vs-scoped.
export const GLOBAL_STATION_ROLES = [
  ROLE.SUPERADMIN,
  ROLE.ADMIN,
  ROLE.AUDITOR,
];

// Canonical display names per role id (matches the backend matrix). Used as a
// fallback when the token doesn't carry roles.name.
export const ROLE_NAMES = {
  1: 'Superadmin',
  2: 'Manager',
  3: 'Cluster Manager',
  4: 'Admin',
  5: 'Auditor',
  6: 'Manufacture',
  7: 'Buses Manager',
  8: 'Buses Accountant',
  9: 'Manufacture Accountant',
};

export const roleIdOf = (token) => Number(token?.user_record?.roles?.id);

// Manufacture and Buses Manager are NOT separate backend role ids - per
// how users are actually created, the backend always issues roles.id: 2
// (Manager) for these; only roles.name differs ("Manufacture" /
// "Buses Manager" vs a plain "Manager"). PAGE_ACCESS above is keyed on 6
// and 7 purely as frontend-only sentinels standing in for "id 2 with this
// specific name" - this is the ONLY place that mapping happens. roleIdOf()
// itself is deliberately left alone (still returns the real numeric id),
// since station-scoping and everything else keyed on id===2 elsewhere in
// this app should keep treating these exactly as an ordinary Manager -
// which they genuinely are, once you get past which nav items show.
const MANUFACTURE_ROLE_NAME = 'manufacture';
const BUSES_MANAGER_ROLE_NAME = 'buses manager';
const BUSES_ACCOUNTANT_ROLE_NAME = 'buses accountant';
// Backend currently spells it "manifacture accountant" - both spellings are
// accepted so fixing the typo on the backend later won't break anything.
const MANUFACTURE_ACCOUNTANT_ROLE_NAMES = [
  'manufacture accountant',
  'manifacture accountant',
];

const effectivePageRoleId = (token) => {
  const id = roleIdOf(token);
  if (id !== ROLE.MANAGER) return id;
  const name = (token?.user_record?.roles?.name || '').trim().toLowerCase();
  if (name === MANUFACTURE_ROLE_NAME) return ROLE.MANUFACTURE;
  if (name === BUSES_MANAGER_ROLE_NAME) return ROLE.BUSES_MANAGER;
  if (name === BUSES_ACCOUNTANT_ROLE_NAME) return ROLE.BUSES_ACCOUNTANT;
  if (MANUFACTURE_ACCOUNTANT_ROLE_NAMES.includes(name)) return ROLE.MANUFACTURE_ACCOUNTANT;
  return id;
};

// Display name for the logged-in user's role, translated. Prefers the
// backend's own roles.name, falling back to the canonical map by id. The
// backend's spelling/case can differ ("auditor", "manifacture accountant"),
// so it is matched to the canonical name first - that is the ar.js key.
const canonicalRoleName = (name) => {
  const n = String(name).trim().toLowerCase();
  if (MANUFACTURE_ACCOUNTANT_ROLE_NAMES.includes(n)) return ROLE_NAMES[ROLE.MANUFACTURE_ACCOUNTANT];
  return Object.values(ROLE_NAMES).find((r) => r.toLowerCase() === n) || String(name).trim();
};

export const roleName = (token) => {
  const name = token?.user_record?.roles?.name || ROLE_NAMES[roleIdOf(token)];
  return name ? tr(canonicalRoleName(name)) : tr('Not logged in.');
};

export const canSeePage = (token, key) =>
  (PAGE_ACCESS[key] || []).includes(effectivePageRoleId(token));

export const canManageUsers = (token) => canSeePage(token, 'Users');

export const hasGlobalStations = (token) =>
  GLOBAL_STATION_ROLES.includes(roleIdOf(token));

// Roles that should see the station filter picker at all. Global roles see
// every station; Cluster Manager (bound to MULTIPLE stations) also needs to
// narrow down among their own. Manager (bound to ONE station) doesn't need a
// picker — there's nothing to filter.
export const canFilterStations = (token) =>
  hasGlobalStations(token) || roleIdOf(token) === ROLE.CLUSTER_MANAGER;

// associated_station can arrive as an array (newer), a comma-joined string
// (observed live: "10TZ139421,10TZ100006"), a single string, or an object with
// .serial_number (legacy). Normalize all of them to a clean string[].
export const associatedSerials = (token) => {
  let a = token?.user_record?.associated_station;
  // ★ The /auth/refresh response DROPS associated_station from user_record (it
  // only survives inside the JWT). LogoutTimeout overwrites the stored token on
  // every nav, so after the first navigation user_record.associated_station is
  // undefined. Fall back to decoding it from the access token's `sub` claim so
  // station scoping + auto-login links keep working across refreshes.
  if (a == null && token?.access_token) {
    try {
      const part = token.access_token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const sub = JSON.parse(decodeURIComponent(escape(atob(part))))?.sub;
      a = sub?.associated_station;
    } catch (e) { /* malformed token — fall through to [] */ }
  }
  if (Array.isArray(a)) return a.map((s) => String(s).trim()).filter(Boolean);
  if (a && typeof a === 'object' && a.serial_number) return [String(a.serial_number)];
  if (typeof a === 'string') return a.split(',').map((s) => s.trim()).filter(Boolean);
  return [];
};

// The serials this user may select/see. `null` == all stations (global role);
// otherwise the explicit list of their associated stations.
export const allowedSerials = (token) =>
  hasGlobalStations(token) ? null : associatedSerials(token);

// Filter a /station/all list down to what `token` is allowed to see. Global
// roles get the list unchanged.
export const scopeStations = (token, stations) => {
  const allowed = allowedSerials(token);
  if (!allowed) return stations;
  const set = new Set(allowed);
  return (stations || []).filter((s) => set.has(s.serial_number));
};